'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import Typography from 'src/components/design-system/Typography'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import ChevronDown from 'public/icons/interface/select.svg'

export default function OpenAccessSelect({
  options = [],
  value,
  onChange = () => {},
  placeholder = null,
  className = '',
}) {
  const translateEntry = useTranslateEntry()
  const [open, setOpen] = useState(false)
  const containerRef = useRef(null)
  const buttonRef = useRef(null)

  const currentOption = useMemo(() => options.find((option) => option.value === value), [options, value])
  const label = currentOption?.label || placeholder || translateEntry('T_SELECT')

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!containerRef.current) return
      if (!containerRef.current.contains(event.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (option) => {
    onChange(option)
    setOpen(false)
    buttonRef.current?.focus()
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        className="cursor-pointer flex w-full items-end justify-between bg-basic-white px-0 py-8 text-left"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <div className="flex flex-col leading-160">
          <Typography variant="body3">{translateEntry('T_SORTING')}</Typography>
          <Typography variant="body3-normal">{label}</Typography>
        </div>
        <ChevronDown
          className={`size-21 text-dark-blue-dark-blue2 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>

      {open && (
        <div
          className="absolute right-0 top-[calc(100%+8px)] z-20 w-full min-w-[220px] max-w-[260px] rounded-4 border border-basic-gray10 bg-basic-white shadow-lg"
          data-lenis-prevent
          role="listbox"
        >
          <div className="flex items-center gap-8 px-16 py-12">
            <Typography variant="body3-normal">{translateEntry('T_SORT_BY')}</Typography>
          </div>
          <div className="divide-y divide-basic-gray10">
            {options.map((option) => {
              const isSelected = option.value === value
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleSelect(option)}
                  className="cursor-pointer flex w-full items-center gap-8 px-16 py-12 text-left transition-colors supports-hover:hover:bg-dark-blue-dark-blue6/30"
                >
                  <span
                    className={`relative inline-flex size-16 items-center justify-center rounded-full border ${
                      isSelected ? 'border-basic-black' : 'border-basic-gray4'
                    }`}
                    aria-hidden
                  >
                    {isSelected && <span className="size-8 rounded-full bg-basic-black" />}
                  </span>
                  <Typography variant="body3-normal">{option.label}</Typography>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
